import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { PAYOUT_TYPES } from "./wbsConfig";
import { normalizeSwitchPolicy } from "./switchLink";

const PAYOUT_LABEL = {
  manhour: "Manhour lines (payroll)",
  material: "Material lines (vendor)",
  subcontract: "Subcontract lines (subcontractor)",
};

const byPayoutTypeDraft = (values) =>
  Object.fromEntries(PAYOUT_TYPES.map((type) => [type, String(values[type])]));

const toDraft = (policy, lines) => ({
  approvalDays: String(policy.approvalDays),
  clientLagDays: String(policy.clientLagDays),
  vendorLagDays: byPayoutTypeDraft(policy.vendorLagDays),
  packetDays: String(policy.packetDays),
  markupPct: byPayoutTypeDraft(policy.markupPct),
  resubmittals: Object.fromEntries(
    lines.map((line) => [line.code, String(policy.resubmittals[line.code] ?? 0)]),
  ),
});

function NumberField({ label, value, onChange, helperText, min = 0, step = 1 }) {
  return (
    <TextField
      label={label}
      type="number"
      size="small"
      fullWidth
      value={value}
      onChange={(event) => onChange(event.target.value)}
      helperText={helperText}
      inputProps={{ min, step }}
    />
  );
}

function PayoutTypeFields({ title, values, onChange, step }) {
  return (
    <>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Grid container spacing={2}>
        {PAYOUT_TYPES.map((type) => (
          <Grid item xs={12} sm={4} key={type}>
            <NumberField
              label={PAYOUT_LABEL[type]}
              value={values[type]}
              step={step}
              onChange={(value) => onChange({ ...values, [type]: value })}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
}

/**
 * Edits the switch model's policy: how long the client takes to approve done
 * work (and which lines were sent back, adding another approval round each),
 * the packet size man-hour lines are billed in, the markup that turns cost
 * into client value, and the two payment lags - the client's, and after THAT
 * the vendor's or payroll's, by payout type since that's what actually sets
 * the terms. Saved alongside this revision's entered cost data (see
 * switchLink.js's setSwitchPolicyInMis).
 */
export default function SwitchPolicyDialog({ open, onClose, policy, lines = [], onSave }) {
  const [draft, setDraft] = useState(() => toDraft(policy, lines));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(toDraft(policy, lines));
      setError("");
    }
  }, [open, policy, lines]);

  const setField = (key) => (value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    const normalized = normalizeSwitchPolicy(draft);
    setSaving(true);
    setError("");
    try {
      await onSave(normalized);
      onClose();
    } catch (saveError) {
      console.error("Failed to save the switch policy:", saveError);
      setError("Couldn’t save to the plant MIS record. Nothing was changed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>Cash flow policy</DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 2 }}>
        <Alert severity="info" variant="outlined">
          The vendor/payroll date is always scheduled off the client-pay date,
          never off the work date — that ordering is what keeps cash held
          non-negative.
        </Alert>

        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <NumberField
              label="Client approval"
              value={draft.approvalDays}
              onChange={setField("approvalDays")}
              helperText="Days from work done to the client verifying it — the switch fires and the line is billed"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <NumberField
              label="Client payment terms"
              value={draft.clientLagDays}
              onChange={setField("clientLagDays")}
              helperText="Days from the switch firing to the client payment being due"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <NumberField
              label="Labour packet size"
              value={draft.packetDays}
              min={1}
              onChange={setField("packetDays")}
              helperText="Man-hour lines are billed in packets of this many days, each with its own switch"
            />
          </Grid>
        </Grid>

        <PayoutTypeFields
          title="Vendor / payroll terms (days after the client payment is due)"
          values={draft.vendorLagDays}
          onChange={setField("vendorLagDays")}
        />

        <PayoutTypeFields
          title="Markup (%) — client value = cost × (1 + markup)"
          values={draft.markupPct}
          step={0.5}
          onChange={setField("markupPct")}
        />

        {lines.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary">
              Resubmittals — times the client sent a line back; each adds
              another approval period before its (last) switch fires
            </Typography>
            <Grid container spacing={1.5}>
              {lines.map((line) => (
                <Grid item xs={6} sm={4} md={3} key={line.code}>
                  <NumberField
                    label={`${line.displayCode} ${line.desc}`}
                    value={draft.resubmittals[line.code] ?? "0"}
                    onChange={(value) =>
                      setDraft((prev) => ({
                        ...prev,
                        resubmittals: { ...prev.resubmittals, [line.code]: value },
                      }))
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
